import { WebGLRenderTarget, OrthographicCamera, Vector2 } from "three";
import { quadVert, quadFrag } from "~/assets/shaders/about/quad";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { LUTPass } from "three/addons/postprocessing/LUTPass.js";
import { GammaCorrectionShader } from "three/examples/jsm/shaders/GammaCorrectionShader.js";
import { LUTCubeLoader } from "three/addons/loaders/LUTCubeLoader.js";
import { suctionFrag } from "~/assets/shaders/about/suction";
import EaseNumber from "~/lib/EaseNumber.js";

import { about, PHRASE } from "./index.js";
import { config } from "./config";

export default class PostProcessor {
    constructor(renderer, scene, camera, mask) {
        this.renderer = renderer;
        this.scene = scene;
        this.camera = camera;
        this.mask = mask;

        this.bloomStrength = new EaseNumber(0, 0.005);
        this.suctionStrength = new EaseNumber(0, 0.005);

        this.maskTarget = new WebGLRenderTarget(
            window.innerWidth,
            window.innerHeight,
        );
        this.quadCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);

        // this.numDrawCalls = 0;

        this._phraseChanged = this._phraseChanged.bind(this);
        about.on("phraseChanged", this._phraseChanged);
    }

    async init() {
        const { renderer, scene, camera } = this;
        const dpr = renderer.getPixelRatio();
        const effectResolutionScale = 0.75;

        this.composer = new EffectComposer(renderer);
        this.composer.setPixelRatio(dpr * effectResolutionScale);

        this.maskPass = new ShaderPass({
            uniforms: {
                tDiffuse: { value: null },
                maskTex: { value: null },
                reverse: { value: 0 },
            },
            vertexShader: quadVert,
            fragmentShader: quadFrag,
        });

        this.bloomPass = new UnrealBloomPass(
            new Vector2(
                window.innerWidth * dpr,
                window.innerHeight * dpr,
            ),
            config.bloom.strength,
            config.bloom.radius,
            config.bloom.threshold,
        );

        this.suctionPass = new ShaderPass({
            uniforms: {
                tDiffuse: { value: null },
                strength: { value: 10 },
                suctionPointUV: { value: [0, 0] },
                aspectRatio: { value: null },
            },
            vertexShader: quadVert,
            fragmentShader: suctionFrag,
        });
        this.lutPass = new LUTPass();
        // this.lut = await this._loadLUT();

        const renderPass = new RenderPass(scene, camera);
        this.composer.addPass(renderPass);
        this.composer.addPass(this.maskPass);
        this.composer.addPass(new ShaderPass(GammaCorrectionShader));
        this.composer.addPass(this.suctionPass);

        // this.composer.addPass(this.bloomPass);
        // this.composer.addPass(this.lutPass);
    }

    update() {
        const isChargingOrBoosting = about.isChargingOrBoosting;
        this.maskPass.uniforms.maskTex.value = this.maskTarget.texture;
        this.maskPass.uniforms.reverse.value = config.mask.reverse ? 1 : 0;
        this.bloomPass.enabled = config.bloom.enable;
        if (config.bloom.enable) {
            this.bloomStrength.value = isChargingOrBoosting ? 1 : 0;
            this.bloomPass.strength =
                config.bloom.strength * this.bloomStrength.value;
            this.bloomPass.radius =
                config.bloom.radius * this.bloomStrength.value;
        }
        this.lutPass.enabled = config.lut.enable && config.bloom.enable;
        if (this.lutPass.enabled) {
            this.lutPass.lut = this.lut;
            this.lutPass.intensity = this.bloomStrength.value;
        }
        this.suctionPass.enabled = config.suction.enable;
        if (config.suction.enable) {
            // this.suctionStrength.value = isChargingOrBoosting ? 1 : 0;
            this.suctionStrength.value = 1;
            this.suctionPass.uniforms.strength.value =
                this.suctionStrength.value;
        }
    }
    render() {
        this.renderer.setRenderTarget(this.maskTarget);
        this.renderer.render(this.mask.scene, this.mask.camera);

        this.renderer.setRenderTarget(null); // reset from mask target to main composer
        this.composer.render();
    }

    setSize(width, height) {
        const dpr = this.renderer.getPixelRatio();
        this.composer.setSize(width, height);
        this.maskTarget.setSize(width * dpr, height * dpr);
    }

    destroy() {
        while (this.composer.passes.length) {
            const pass = this.composer.passes.pop();
            pass.dispose?.();
            pass.lut?.dispose();
            pass.material?.dispose();
        }

        this.composer.dispose();
        this.maskTarget.dispose();
        this.bloomStrength.destroy();
        this.suctionStrength.destroy();

        about.off("phraseChanged", this._phraseChanged);
    }

    translate(x, y) {
        this.suctionPass.uniforms.suctionPointUV.value = [x, y];
        this.suctionPass.uniforms.aspectRatio.value =
            window.innerWidth / window.innerHeight;
    }

    _loadLUT() {
        return new Promise((resolve, reject) => {
            new LUTCubeLoader().load(
                "/about/contrast.CUBE",
                (res) => {
                    resolve(res.texture3D);
                },
                undefined,
                (err) => {
                    // console.error("LUTCubeLoader error");
                    // console.error(err);
                    reject(err);
                },
            );
        });
    }

    _phraseChanged = (pPhrase, phrase) => {
        if (pPhrase === PHRASE.BOOSTING && phrase === PHRASE.ENDING) {
            this.suctionStrength.setTo(config.suction.strength);
        }
    };
}
